import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomerMapLibreMap from 'components/MapLibre/CustomerMapLibreMap';
import ExpeditionTrackLegend from 'components/Map/ExpeditionTrackLegend';
import CortadLegend from 'components/Map/CortadLegend';
import { SidebarFilterContext } from 'contexts/SidebarFilterContext';
import filterColonies from 'utils/filterColonies';
import { Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'components/Map/CortadTimeline.css';

const Map = ({
  basemap = 'imagery',
  captionsVisible = true,
  benthicVisible = true,
  benthicClasses = {},
  reefExtentVisible = false,
  reefExtentClasses = {},
  bleachingVisible = false,
  bleachingYear = null,
  bleachingObservationsGeoJson = null,
  onBleachingObservationClick,
  scopedColonies = null,
  skipUrlFocus = false,
  expeditionTrackGeoJson = null,
  expeditionTrackVisible = false,
  expeditionTrackFitBounds = false,
  colonyFitBounds = true,
  cortadVisible = false,
  cortadVariable = 'tsa_dhw',
  cortadMonth = '2016-08',
  cortadMonths = [],
  onCortadMonthChange,
  cortadMeta = null,
  onCortadStatusChange,
  selectMode = false,
  zoneBbox = null,
  onZoneBboxChange,
  onSelectModeChange,
}) => {
  const { allColonies, filters, setFilteredColonies, defaultValues } =
    useContext(SidebarFilterContext);
  const [ready, setReady] = useState(false);
  const [cortadStatus, setCortadStatus] = useState({ loading: false, zoomTooLow: false });
  const [searchParams] = useSearchParams();

  const colonySource = scopedColonies ?? allColonies;

  const computedColonies = useMemo(() => {
    if (!colonySource || colonySource.length === 0) return [];
    if (filters && Object.keys(filters).length > 0) {
      return filterColonies(filters, colonySource, defaultValues);
    }
    return colonySource;
  }, [colonySource, filters, defaultValues]);

  const focusTarget = useMemo(() => {
    if (skipUrlFocus) return null;
    const colonyParam = searchParams.get('colony');
    const lngParam = searchParams.get('lng');
    const latParam = searchParams.get('lat');
    const zoomParam = searchParams.get('zoom');
    const colonyId = colonyParam ? Number(colonyParam) : NaN;
    const lng = lngParam ? Number(lngParam) : NaN;
    const lat = latParam ? Number(latParam) : NaN;
    const zoom = zoomParam ? Number(zoomParam) : 12;
    const colonies = Array.isArray(allColonies) ? allColonies : [];
    const colony = Number.isFinite(colonyId)
      ? colonies.find((item) => item.id === colonyId)
      : null;

    const targetLng = Number.isFinite(lng) ? lng : colony?.longitude;
    const targetLat = Number.isFinite(lat) ? lat : colony?.latitude;
    if (!Number.isFinite(targetLng) || !Number.isFinite(targetLat)) return null;

    return {
      key: `${colonyId || 'point'}-${targetLng}-${targetLat}-${zoom}`,
      colony,
      coordinates: [targetLng, targetLat],
      zoom: Number.isFinite(zoom) ? zoom : 12,
    };
  }, [allColonies, searchParams, skipUrlFocus]);

  const handleCortadStatusChange = useCallback((status) => {
    const next = {
      loading: Boolean(status?.loading),
      zoomTooLow: Boolean(status?.zoomTooLow),
    };
    setCortadStatus((prev) => (
      prev.loading === next.loading && prev.zoomTooLow === next.zoomTooLow ? prev : next
    ));
    onCortadStatusChange?.(next);
  }, [onCortadStatusChange]);

  useEffect(() => {
    if (!cortadVisible) {
      setCortadStatus({ loading: false, zoomTooLow: false });
      onCortadStatusChange?.({ loading: false, zoomTooLow: false });
    }
  }, [cortadVisible, onCortadStatusChange]);

  useEffect(() => {
    if (scopedColonies) {
      setFilteredColonies(computedColonies);
      setReady(true);
      return;
    }
    if (!allColonies || allColonies.length === 0) {
      setReady(false);
      return;
    }
    setFilteredColonies(computedColonies);
    setReady(true);
  }, [scopedColonies, allColonies, computedColonies, setFilteredColonies]);

  if (!ready) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          backgroundColor: '#f8f9fa',
        }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <CustomerMapLibreMap
        basemap={basemap}
        captionsVisible={captionsVisible}
        benthicVisible={benthicVisible}
        benthicClasses={benthicClasses}
        reefExtentVisible={reefExtentVisible}
        reefExtentClasses={reefExtentClasses}
        bleachingVisible={bleachingVisible}
        bleachingYear={bleachingYear}
        bleachingObservationsGeoJson={bleachingObservationsGeoJson}
        onBleachingObservationClick={onBleachingObservationClick}
        colonies={computedColonies}
        focusTarget={focusTarget}
        expeditionTrackGeoJson={expeditionTrackGeoJson}
        expeditionTrackVisible={expeditionTrackVisible}
        expeditionTrackFitBounds={expeditionTrackFitBounds}
        colonyFitBounds={colonyFitBounds}
        cortadVisible={cortadVisible}
        cortadVariable={cortadVariable}
        cortadMonth={cortadMonth}
        onCortadStatusChange={handleCortadStatusChange}
        selectMode={selectMode}
        zoneBbox={zoneBbox}
        onZoneBboxChange={onZoneBboxChange}
        onSelectModeChange={onSelectModeChange}
      />
      {cortadVisible && cortadStatus.zoomTooLow && (
        <div className="cortad-status-chip" role="status">
          Zoom in toward a reef to display temperature tiles.
        </div>
      )}
      {cortadVisible && cortadStatus.loading && !cortadStatus.zoomTooLow && (
        <div className="cortad-status-chip is-loading" role="status">
          <span className="cortad-status-spinner" aria-hidden="true" />
          Loading temperature tiles… the first view can take a while.
        </div>
      )}
      {cortadVisible && (
        <CortadLegend variable={cortadVariable} meta={cortadMeta} />
      )}
      <ExpeditionTrackLegend
        visible={expeditionTrackVisible && expeditionTrackGeoJson?.features?.length > 0}
      />
    </div>
  );
};

export default Map;
